/**
 * @module login
 * @description Login-page email-form behavior.
 *
 * - If `cli_callback` is present and the user is already authenticated:
 *   immediately creates an API key and redirects to the callback URL with
 *   ?key=<rawKey>, completing the CLI login flow without email re-entry.
 * - Redirects to / if the user is already authenticated (no cli_callback).
 * - Submits the email to POST /auth/login and shows a confirmation
 *   "check your email" panel on success.
 * - If a `cli_callback` query parameter is present (set by `hostlocal-tunnel login`),
 *   it is forwarded in the POST body so the server can redirect back to the CLI
 *   listener after the magic link is clicked.
 * Built into /js/login.js by scripts/build-client.js.
 */
export {};

// Read cli_callback early so it is in scope for the auth/me handler below.
const cliCallbackEarly = new URLSearchParams(window.location.search).get('cli_callback') ?? '';

// If already authenticated, complete the CLI flow immediately or bounce to home.
fetch('/auth/me')
  .then(async (r) => {
    if (!r.ok) return; // not logged in — show the form
    if (cliCallbackEarly) {
      // Already authenticated + CLI flow: create an API key and redirect to callback.
      const apiRes = await fetch('/auth/api-key/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'hostlocal-tunnel CLI' }),
      });
      if (apiRes.ok) {
        const body = (await apiRes.json()) as { key: string };
        const sep = cliCallbackEarly.includes('?') ? '&' : '?';
        window.location.replace(`${cliCallbackEarly}${sep}key=${encodeURIComponent(body.key)}`);
      } else {
        // Key creation failed — fall back to normal login form.
        window.location.replace('/');
      }
    } else {
      window.location.replace('/');
    }
  })
  .catch(() => {
    /* ignore — show login form */
  });

const form = document.getElementById('login-form') as HTMLFormElement | null;
const emailInput = document.getElementById('login-email') as HTMLInputElement | null;
const errorEl = document.getElementById('login-error') as HTMLElement | null;
const formWrap = document.getElementById('login-form-wrap') as HTMLElement | null;
const confirmWrap = document.getElementById('login-confirm') as HTMLElement | null;
const confirmEmail = document.getElementById('login-confirm-email') as HTMLElement | null;
const submitBtn = document.getElementById('login-submit-btn') as HTMLButtonElement | null;

// Read cli_callback from query string (set by `hostlocal-tunnel login` browser flow).
const cliCallback = cliCallbackEarly;

if (form && emailInput && errorEl && formWrap && confirmWrap && confirmEmail && submitBtn) {
  form.addEventListener('submit', async () => {
    const email = emailInput.value.trim();
    if (!email) {
      errorEl.textContent = 'Please enter your email address.';
      errorEl.style.display = 'block';
      emailInput.focus();
      return;
    }
    errorEl.style.display = 'none';
    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending\u2026';
    try {
      const body: Record<string, string> = { email };
      if (cliCallback) body['cli_callback'] = cliCallback;
      const res = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        confirmEmail.textContent = email;
        formWrap.style.display = 'none';
        confirmWrap.style.display = 'block';
        return;
      }
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (res.status === 503) {
        errorEl.textContent = 'Login is not available right now.';
      } else if (data.error) {
        errorEl.textContent = data.error;
      } else {
        errorEl.textContent = 'Something went wrong. Please try again.';
      }
      errorEl.style.display = 'block';
    } catch {
      errorEl.textContent = 'Network error. Please try again.';
      errorEl.style.display = 'block';
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Send link';
    }
  });

  emailInput.addEventListener('input', () => {
    errorEl.style.display = 'none';
  });
}
